import { ApolloQueryResult, useQuery } from '@apollo/client';
import { BreadcrumbItem } from '@css-xp/base/breadcrumbs';
import {
  EntitlementDetails,
  Maybe,
  PaymentMethod,
  FetchLinkedEntitlementsQuery,
  FetchLinkedEntitlementsQueryVariables,
  LinkedEntitlementTypes,
  LinkedDestinationEntitlement,
  FetchLinkedAppsQuery,
  FetchLinkedAppsQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { CCP_ENTITLEMENT_PATH_2, STATUSPAGE_PATH_2 } from '@atlassian/cssxp-typescommon';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import defaultTo from 'lodash/defaultTo';
import React, { useCallback, useMemo } from 'react';
import { EntitlementPageUtility, EntitlementSummary } from '../../../entitlement-page-utility';
import { StatuspageHeader, StatuspageHeaderActions } from './entitlement-page-header';
import EntitlementPageTabs from './entitlement-page-tabs';
import { getParentContainerId } from '@atlassian/cssxp-utils-entitlement';
import { fetchLinkedEntitlements } from '../queries/fetch-linked-entitlements';
import { currentEntitlementType } from '@css-xp/base/entitlement-utils';
import { fetchLinkedApps } from '../queries/fetch-linked-apps';
// @ts-ignore
import AtlassianPartnerIcon from '@css-xp/base/assets/img/icon-atlassian-partner.svg';

interface Props {
  entitlement: EntitlementDetails;
  availablePaymentMethods: Maybe<Maybe<PaymentMethod>[]> | undefined;
  refetchAll: () => void;
  isPartnerAccount: boolean;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}

export const EntitlementPageComponents = ({
  entitlement,
  refetchAll,
  refetch,
  availablePaymentMethods,
  isPartnerAccount,
}: Props) => {
  const { isApp, id, isSandbox } = entitlement;

  const { data, loading: parentEntitlementsLoading } = useQuery<
    FetchLinkedEntitlementsQuery,
    FetchLinkedEntitlementsQueryVariables
  >(fetchLinkedEntitlements, {
    variables: {
      id,
      searchLinkedEntitlementParams: {
        entitlementId: id,
        containerId: getParentContainerId(entitlement),
        currentEntitlementType: isSandbox
          ? LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT
          : LinkedEntitlementTypes.PROD_ENTITLEMENT_APP,
        destinationEntitlementType: isSandbox
          ? [LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT]
          : [LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT],
        productType: entitlement.productType,
        findProducts: true,
        findApps: false,
      },
    },
    displayName: 'fetchAppEntitlements',
    notifyOnNetworkStatusChange: true,
    skip: !isApp,
  });

  const { data: linkedAppsData } = useQuery<FetchLinkedAppsQuery, FetchLinkedAppsQueryVariables>(fetchLinkedApps, {
    variables: {
      id: entitlement.id,
      searchLinkedEntitlementParams: {
        entitlementId: entitlement.id,
        containerId: getParentContainerId(entitlement),
        productType: entitlement.productType,
        currentEntitlementType: currentEntitlementType(entitlement),
        destinationEntitlementType: entitlement.isSandbox
          ? [LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT]
          : [LinkedEntitlementTypes.PROD_ENTITLEMENT_APP],
        findApps: true,
        findProducts: false,
      },
    },
    displayName: 'fetchAppEntitlements',
    notifyOnNetworkStatusChange: true,
    skip: !!entitlement.isApp,
  });

  const appCount = useMemo(() => {
    return linkedAppsData?.entitlementDetails.linkedEntitlements?.length;
  }, [linkedAppsData]);

  const parentProductEntitlements = useMemo(() => {
    return data?.entitlementDetails.linkedEntitlements ?? [];
  }, [data]);

  const getBreadcrumbs = useCallback((): BreadcrumbItem.BreadcrumbItem[] => {
    const cloudSiteName = entitlement?.cloudSiteCustomHostname ?? entitlement?.cloudSiteHostname;

    return [
      { title: defaultTo(cloudSiteName, 'No hostname provided'), href: undefined },
      { title: 'Subscriptions', href: undefined },
      {
        title: `${defaultTo(entitlement?.productName, '')} ${defaultTo(entitlement?.entitlementNumber, '')}`,
        href:
          entitlement?.productId && isStatuspage(entitlement.productId)
            ? `${STATUSPAGE_PATH_2}/${encodeURIComponent(defaultTo(entitlement?.entitlementNumber, ''))}`
            : `${CCP_ENTITLEMENT_PATH_2}/${encodeURIComponent(defaultTo(entitlement?.entitlementNumber, ''))}`,
      },
    ];
  }, [entitlement]);

  const renderSubHeaderItems = () => (
    <div className="ent-page-subheader">
      {isPartnerAccount && (
        <span data-testid="partner-logo">
          {/*// @ts-ignore */}
          <img src={AtlassianPartnerIcon} height={'15px'} alt="Atlassian partner icon" />
        </span>
      )}
    </div>
  );

  return (
    <>
      <EntitlementPageUtility.Breadcrumbs items={getBreadcrumbs()} />
      <EntitlementPageUtility.Header
        header={<StatuspageHeader entitlement={entitlement} />}
        subheader={renderSubHeaderItems()}
        actions={
          <StatuspageHeaderActions
            entitlement={entitlement}
            parentProductEntitlements={parentProductEntitlements as LinkedDestinationEntitlement[]}
            refetch={refetchAll}
          />
        }
        entitlement={entitlement}
      />
      <EntitlementSummary
        entitlement={entitlement}
        refetch={refetch}
        appCount={appCount}
        parentProductEntitlements={parentProductEntitlements as LinkedDestinationEntitlement[]}
      />
      <EntitlementPageTabs
        statuspageEntitlement={{ ...entitlement }}
        availablePaymentMethods={availablePaymentMethods}
        refetch={refetch}
        parentProductEntitlements={parentProductEntitlements as LinkedDestinationEntitlement[]}
        parentEntitlementsLoading={parentEntitlementsLoading}
        isPartnerAccount={isPartnerAccount}
      />
    </>
  );
};
